import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { computed, ref } from "vue";

// =============================================================================
// Identitet kommer från JWT-token i localStorage (vi har bara userId/name där).
// Behörigheter (roller och venue) kommer ALDRIG från token - de hämtas alltid
// via GET /api/auth/me så att frontend ser nya roller efter en reload, inte
// först efter re-login.
// =============================================================================

const isLoggedIn = ref(!!localStorage.getItem("token"));
const tokenPayload = ref(decodeStoredToken());

// Hydrerad användarprofil från /me. null = inte inläst/inte inloggad.
const userProfile = ref(null);
// Flagga som router och App.vue kan await:a på innan de fattar beslut om
// vart de ska rendera vad.
const isHydrated = ref(false);
let hydratePromise = null;

function decodeStoredToken() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp && decoded.exp * 1000 < Date.now()) return null;
    return decoded;
  } catch (e) {
    return null;
  }
}

/**
 * Hämtar färska roller och venue från /api/auth/me. Cacheas i userProfile.
 * Vid 401/403 invaliderar vi tokenet (det är ogiltigt eller borttaget).
 *
 * Anropet är idempotent och delar promise om det körs flera gånger samtidigt.
 */
export function hydrate({ force = false } = {}) {
  if (hydratePromise && !force) return hydratePromise;

  hydratePromise = (async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      userProfile.value = null;
      isHydrated.value = true;
      return null;
    }
    try {
      const { data } = await axios.get(
        process.env.VUE_APP_API_URL + "auth/me",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      userProfile.value = data;
      return data;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Ogiltig token - rensa identitet helt
        localStorage.removeItem("token");
        isLoggedIn.value = false;
        tokenPayload.value = null;
      }
      userProfile.value = null;
      return null;
    } finally {
      isHydrated.value = true;
    }
  })();

  return hydratePromise;
}

/**
 * Anropas av router-guarden innan den fattar beslut om admin-åtkomst.
 * Säkerställer att första render av en privilegierad route inte sker mot
 * en tom userProfile.
 */
export function waitForHydration() {
  if (isHydrated.value) return Promise.resolve(userProfile.value);
  return hydrate();
}

export function checkLoginStatus() {
  const decoded = decodeStoredToken();
  isLoggedIn.value = !!decoded;
  tokenPayload.value = decoded;
  return isLoggedIn.value;
}

/**
 * Spara nytt token och hydrera profilen direkt. Använd från Login,
 * Register och AuthCallback efter lyckad inloggning.
 *
 * VIKTIGT: vi flippar INTE tillbaka isHydrated till false här. Den är en
 * envägsflagga som markerar "har vi laddat klart minst en gång". Om vi
 * satte den till false hade App.vue:s v-if dolt router-view igen, vilket
 * skulle avmontera AuthCallback mitt i await:et och remounta den efter
 * hydration - och då hittas ingen token i hashen längre eftersom vi
 * redan har replaceState:at bort den.
 */
export async function setToken(token) {
  localStorage.setItem("token", token);
  checkLoginStatus();
  // Tvinga ny hydration så vi inte återanvänder en gammal cachead promise
  hydratePromise = null;
  return hydrate({ force: true });
}

/**
 * Rensa identitet + cachead profil. Använd vid logout.
 */
export function clearAuth() {
  localStorage.removeItem("token");
  userProfile.value = null;
  hydratePromise = null;
  isHydrated.value = true;
  checkLoginStatus();
}

// Reaktiva selektorer - läs alltid från den hydrerade profilen, inte token.
const userRoles = computed(() => userProfile.value?.roles || []);
const userVenue = computed(() => userProfile.value?.venue || null);
const isSuperAdmin = computed(() => userRoles.value.includes("super-admin"));
const isOrganizer = computed(() => userRoles.value.includes("organizer"));

function hasRole(role) {
  return userRoles.value.includes(role);
}

export {
  isLoggedIn,
  tokenPayload,
  userProfile,
  userRoles,
  userVenue,
  isHydrated,
  isSuperAdmin,
  isOrganizer,
  hasRole,
};
